import { useRouter } from 'next/router';
import queryString from 'query-string';
import {
    DecodedValueMap,
    NumberParam,
    QueryParamConfigMap,
    StringParam,
    encodeQueryParams,
    useQueryParam,
    useQueryParams,
} from 'use-query-params';
import { slugifyTitle } from 'shared';

type SearchHit = {
    id: string;
    fullPreferredTitle: string;
};

export interface StepperData {
    totalHits: number;
    hits: SearchHit[];
}

export const getStepperQueryVars = <FilterEnum>(
    filterParams: DecodedValueMap<QueryParamConfigMap>,
    offset: number,
    query?: string,
    validFilterKeys?: string[]
) => ({
    query: query ?? null,
    offset: offset !== 0 ? offset - 1 : 0,
    limit: 3, // prev, curr, next,
    filters: (Object.entries(filterParams) as [FilterEnum, string[]][])
        .filter(([, value]) => value.length > 0) // remove empty filters
        .filter(([key]) => (validFilterKeys ? validFilterKeys.includes(key as string) : true))
        .map(([key, value]) => ({
            key,
            value,
        })),
});

const useBrowseStepper = (stepperData: StepperData | undefined, filterQueryParams?: QueryParamConfigMap) => {
    const { pathname, push } = useRouter();
    const [filterParams] = useQueryParams(filterQueryParams);
    const [offset] = useQueryParam('offset', NumberParam);
    const [queryParam] = useQueryParam('query', StringParam);
    const prevFilm = stepperData?.hits[0];
    const nextFilm = stepperData?.hits[offset === 0 ? 1 : 2];
    const pageBaseName = pathname.split('/').filter(Boolean)[0];

    return {
        prevFilm,
        nextFilm,
        current: offset + 1,
        total: stepperData?.totalHits,
        offset,
        queryParam,
        onPrev: () =>
            push({
                pathname: `/${pageBaseName}/${prevFilm.id}/${slugifyTitle(prevFilm.fullPreferredTitle)}`,
                search: queryString.stringify({
                    ...encodeQueryParams(filterQueryParams, filterParams),
                    ...(queryParam ? { query: queryParam } : {}),
                    offset: offset - 1,
                }),
            }),
        onNext: () =>
            push({
                pathname: `/${pageBaseName}/${nextFilm.id}/${slugifyTitle(nextFilm.fullPreferredTitle)}`,
                search: queryString.stringify({
                    ...encodeQueryParams(filterQueryParams, filterParams),
                    ...(queryParam ? { query: queryParam } : {}),
                    offset: offset + 1,
                }),
            }),
    };
};

export default useBrowseStepper;
